import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LuArrowLeft, LuArrowRight, LuBookOpen, LuCircleCheck, LuCircleDashed, LuListChecks } from 'react-icons/lu';
import { courseState, percentOf, STATE_LABEL, STATE_TONE } from './courseProgress';
import { Button, ButtonLink } from '../../ui/Button';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Badge, DifficultyLabel, Meter } from '../../ui/Labels';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { courseApi } from '../../lib/courseApi';
import { acceptanceRate } from '../../lib/format';
import { cx } from '../../ui/cx';

const STAGGER_LIMIT = 16;

function StatusIcon({ status }) {
  const t = useStrings();
  if (status === 'solved') {
    return <LuCircleCheck className="problem-status problem-status--solved" aria-label={t('problems.statusSolved')} />;
  }
  if (status === 'attempted') {
    return <LuCircleDashed className="problem-status problem-status--attempted" aria-label={t('problems.statusAttempted')} />;
  }
  return <span className="visually-hidden">{t('problems.statusTodo')}</span>;
}

function CourseStat({ label, value, tone }) {
  return (
    <div className={cx('course-stat', tone && `course-stat--${tone}`)}>
      <dt>{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

function CourseProblems({ courseId, questions, nextId }) {
  const t = useStrings();
  const navigate = useNavigate();
  const open = (problemId) => navigate(`/courses/${courseId}/problems/${problemId}`, { viewTransition: true });

  return (
    <table className="data-table data-table--interactive problem-table course-table">
      <thead>
        <tr>
          <th className="problem-table__status">
            <span className="visually-hidden">{t('problems.columnStatus')}</span>
          </th>
          <th className="problem-table__number">{t('courses.columnStep')}</th>
          <th>{t('problems.columnTitle')}</th>
          <th className="is-numeric problem-table__acceptance">{t('problems.columnAcceptance')}</th>
          <th className="problem-table__difficulty">{t('problems.columnDifficulty')}</th>
          <th className="problem-table__go" aria-hidden="true" />
        </tr>
      </thead>
      <tbody className="stagger">
        {questions.map((question, index) => {
          const rate = acceptanceRate(question.acceptedCount, question.submissionCount);
          const next = question.id === nextId;
          return (
            <tr
              key={question.id}
              className={cx(next && 'is-next')}
              style={index < STAGGER_LIMIT ? { '--i': index } : { animation: 'none' }}
              onClick={() => open(question.id)}
            >
              <td className="problem-table__status">
                <StatusIcon status={question.status} />
              </td>
              <td className="problem-table__number mono">{index + 1}</td>
              <td className="problem-table__title">
                <a
                  href={`/courses/${courseId}/problems/${question.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    open(question.id);
                  }}
                >
                  {question.title}
                </a>
                {next && (
                  <Badge tone="accent" dot className="course-table__next">
                    {t('courses.upNext')}
                  </Badge>
                )}
                {question.tags.length > 0 && (
                  <span className="problem-table__tags">
                    {question.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="problem-table__tag">
                        {tag}
                      </span>
                    ))}
                  </span>
                )}
              </td>
              <td className="is-numeric problem-table__acceptance muted">{rate === null ? '—' : t('problems.acceptance', { rate })}</td>
              <td className="problem-table__difficulty">
                <DifficultyLabel level={question.difficulty} className="difficulty-pill" />
              </td>
              <td className="problem-table__go" aria-hidden="true">
                <LuArrowRight />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function CoursePage() {
  const t = useStrings();
  const { courseId } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useDocumentTitle(state.data?.course.title || t('courses.title'));

  const apply = useCallback(
    (response) =>
      setState(
        response.success
          ? { status: 'ready', data: response.data, error: null }
          : {
              status: response.status === 404 ? 'missing' : 'error',
              data: null,
              error: response.error,
            }
      ),
    []
  );

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: 'loading' }));
    apply(await courseApi.get(courseId));
  }, [apply, courseId]);

  useEffect(() => {
    let cancelled = false;
    courseApi.get(courseId).then((response) => {
      if (!cancelled) apply(response);
    });
    return () => {
      cancelled = true;
    };
  }, [apply, courseId]);

  const summary = useMemo(() => {
    const questions = state.data?.questions ?? [];
    const solved = questions.filter((question) => question.status === 'solved').length;
    const attempted = questions.filter((question) => question.status === 'attempted').length;
    const next = questions.find((question) => question.status !== 'solved') ?? null;
    return { total: questions.length, solved, attempted, next };
  }, [state.data]);

  const back = (
    <Link to="/courses" className="back-link" viewTransition>
      <LuArrowLeft aria-hidden="true" />
      {t('courses.back')}
    </Link>
  );

  if (state.status === 'missing') {
    return (
      <div className="page">
        {back}
        <div className="panel">
          <EmptyState
            icon={LuBookOpen}
            title={t('courses.notFound')}
            action={
              <ButtonLink to="/courses" viewTransition>
                {t('courses.back')}
              </ButtonLink>
            }
          />
        </div>
      </div>
    );
  }

  const { course, questions } = state.data ?? { course: null, questions: [] };
  const progressState = courseState(summary.solved, summary.total);
  const progressLabel = t('courses.progress', { solved: summary.solved, total: summary.total });
  const first = questions[0] ?? null;
  const cta = summary.next
    ? {
        to: `/courses/${course.id}/problems/${summary.next.id}`,
        label: summary.solved + summary.attempted > 0 ? t('courses.continue') : t('courses.start'),
      }
    : first && { to: `/courses/${course.id}/problems/${first.id}`, label: t('courses.review') };

  return (
    <div className="page">
      {back}

      {state.status === 'error' && (
        <Alert tone="danger" action={<Button size="sm" onClick={load}>{t('common.retry')}</Button>}>
          {state.error}
        </Alert>
      )}

      {state.status === 'loading' && !course && (
        <div className="course-loading" aria-hidden="true">
          <Skeleton height={180} />
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      )}

      {course && (
        <>
          <header className="panel lit course-hero" data-state={progressState}>
            <div className="course-hero__main">
              <div className="course-hero__eyebrow">
                <span className="course-hero__icon" aria-hidden="true">
                  <LuBookOpen />
                </span>
                {t('courses.eyebrow')}
                <Badge tone={STATE_TONE[progressState]} dot={progressState === 'active'}>
                  {t(STATE_LABEL[progressState])}
                </Badge>
              </div>
              <h1 className="course-hero__title">{course.title}</h1>
              {course.description && <p className="course-hero__description">{course.description}</p>}
              {cta && (
                <div className="course-hero__actions">
                  <ButtonLink to={cta.to} variant="primary" trail={LuArrowRight} viewTransition>
                    {cta.label}
                  </ButtonLink>
                  {summary.next && <span className="course-hero__next">{summary.next.title}</span>}
                </div>
              )}
            </div>

            <div className="course-hero__progress">
              <div className="course-hero__percent">
                <span className="tabular">{percentOf(summary.solved, summary.total)}</span>
                <span className="course-hero__percent-sign">%</span>
              </div>
              <Meter value={summary.solved} max={summary.total} label={progressLabel} />
              <span className="course-hero__progress-label">{progressLabel}</span>
              <dl className="course-hero__stats">
                <CourseStat label={t('courses.statSolved')} value={summary.solved} tone="solved" />
                <CourseStat label={t('courses.statAttempted')} value={summary.attempted} tone="attempted" />
                <CourseStat label={t('courses.statTodo')} value={summary.total - summary.solved - summary.attempted} />
              </dl>
            </div>
          </header>

          <section className="section course-problems" aria-labelledby="course-problems-title">
            <div className="course-problems__header">
              <h2 id="course-problems-title" className="section-title">
                <LuListChecks aria-hidden="true" />
                {t('courses.problemsTitle')}
              </h2>
              <span className="muted">{t('courses.problemsMeta')}</span>
            </div>
            {questions.length === 0 ? (
              <div className="panel">
                <EmptyState icon={LuListChecks} title={t('courses.emptyProblemsTitle')} text={t('courses.emptyProblemsText')} />
              </div>
            ) : (
              <div className="table-card">
                <CourseProblems courseId={course.id} questions={questions} nextId={summary.next?.id ?? null} />
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
